import { JobViewModel } from "../ViewModels/JobViewModel";
import { StreamContextMenuView } from "./StreamContextMenuView";
import { DashJobService } from "../../../../services/DashJobService";
import { Select, Tag, notification } from "antd";
import { PlusCircleFilled, SearchOutlined, CheckOutlined, CalendarOutlined } from "@ant-design/icons";
import { useState, useRef, useEffect, useCallback } from "react";
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { useDarkMode, useThemeColors } from "redux/hooks";
import { ConditionalTooltip } from "./ConditionalTooltip";
import DashStreamLogo from "../../../icons/DashStreamLogo";
import { TagView } from "../../../assets/utilities/TagView";
import { ScheduleRegenModalView, ScheduleData } from "../../../assets/utilities/ScheduleRegenModalView";

export function JobListView(props: {
    jobs: readonly JobViewModel[];
    tagList: string[];
    anyContextMenuOpen: boolean;
    setAnyContextMenuOpen: (open: boolean) => void;
    onNewStream: () => void;
    modelAlias: string;
    jobService: DashJobService;
    onToggleCollapse: () => void;
    isCollapsed?: boolean;
    onEditDashboard?: (jobID: string, initialValues: { sqlQueries: string[], sizes: number[], dimensions: number, title: string }) => void;
}) {
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const jobList = useRef<HTMLDivElement>(null);

    const [searchValues, setSearchValues] = useState<string[]>([]);
    const [streamContextMenu, setStreamContextMenu] = useState<{ visible: boolean, x: number, y: number, jobID: string | null }>({ visible: false, x: 0, y: 0, jobID: null });
    const streamContextMenuRef = useRef<HTMLDivElement>(null);
    const [renamingStream, setRenamingStream] = useState<{ jobID: string, alias: string } | null>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);
    const renameFormRef = useRef<HTMLDivElement>(null);
    const hasSelectedRef = useRef<string | null>(null);
    const [scheduleModal, setScheduleModal] = useState<{ isOpen: boolean, jobID: string | null }>({ isOpen: false, jobID: null });
    const [openTagPopovers, setOpenTagPopovers] = useState<Set<string>>(new Set());


    const tagRender = (tagProps: CustomTagProps) => {
        const { label, value, closable, onClose } = tagProps;
        const isTag = typeof value === 'string' && props.tagList.includes(value);

        return (
            <Tag
                closable={closable}
                onClose={onClose}
                style={{
                    marginRight: 3,
                    backgroundColor: isTag ? (darkMode ? themeColors.backgroundColorTertiary : '#E6F0F7') : (darkMode ? themeColors.borderColor : undefined),
                    borderColor: isTag ? themeColors.primaryColorLight : (darkMode ? themeColors.borderColorSecondary : undefined),
                    color: darkMode ? themeColors.textColor : undefined,
                }}
            >
                {label}
            </Tag>
        );
    };

    // Get all dashboard jobs (no children/threads)
    const originalJobs = props.jobs.filter(job => !job.replyTarget);



    const handleStreamRightClick = useCallback((event: React.MouseEvent, jobID: string) => {
        event.preventDefault();
        event.stopPropagation();
        setStreamContextMenu({ visible: true, x: event.clientX, y: event.clientY, jobID });
        props.setAnyContextMenuOpen(true);
    }, [props]);


    useEffect(() => {
        if (renamingStream && renameInputRef.current) {
            const input = renameInputRef.current;
            // Only select on initial open, not when user is typing
            if (hasSelectedRef.current !== renamingStream.jobID) {
                input.focus();
                setTimeout(() => {
                    input.select();
                    // Scroll to the beginning (left side) after selection
                    input.setSelectionRange(0, input.value.length);
                    input.scrollLeft = 0;
                }, 0);
                hasSelectedRef.current = renamingStream.jobID;
            }
        } else {
            hasSelectedRef.current = null;
        }
    }, [renamingStream]);

    const handleRenameStream = useCallback((jobID: string) => {
        const job = props.jobs.find(j => j.jobID === jobID && !j.replyTarget);
        if (job) {
            setRenamingStream({ jobID, alias: job.alias });
        }
    }, [props.jobs]);

    const handleRenameSubmit = useCallback(async () => {
        if (!renamingStream) return;

        const newAlias = renamingStream.alias.trim();

        // Check if alias is empty or just whitespace
        if (!newAlias || newAlias.length === 0) {
            notification.error({
                message: 'Invalid dashboard name',
                description: 'Dashboard name cannot be empty.',
                placement: 'bottomLeft',
                duration: 3
            });
            setRenamingStream(null);
            return;
        }

        // Validate: only alphanumeric and specific special characters (including spaces)
        const allowedPattern = /^[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]+$/;
        if (!allowedPattern.test(newAlias)) {
            // Find invalid characters
            const invalidChars = new Set<string>();
            for (const char of newAlias) {
                if (!/[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]/.test(char)) {
                    invalidChars.add(char === ' ' ? '(space)' : char);
                }
            }
            const invalidCharsList = Array.from(invalidChars).join(', ');

            // Invalid characters - show error notification
            notification.error({
                message: 'Invalid dashboard name',
                description: `Invalid characters: ${invalidCharsList}`,
                placement: 'bottomLeft',
                duration: 3
            });
            setRenamingStream(null);
            return;
        }

        try {
            await props.jobService.updateJobAlias(renamingStream.jobID, newAlias);
            // Update the job's alias in the view model
            const job = props.jobs.find(j => j.jobID === renamingStream.jobID && !j.replyTarget);
            if (job) {
                job.alias = newAlias;
            }
            notification.success({
                message: 'Dashboard renamed',
                description: `"${newAlias}"`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            console.error("Failed to update job alias:", error);
            notification.error({
                message: 'Failed to rename dashboard',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }

        setRenamingStream(null);
    }, [renamingStream, props.jobService, props.jobs]);

    const handleRenameCancel = useCallback(() => {
        setRenamingStream(null);
    }, []);

    const handleRenameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleRenameSubmit();
        } else if (e.key === 'Escape') {
            handleRenameCancel();
        }
    }, [handleRenameSubmit, handleRenameCancel]);

    const handleEditDashboard = useCallback(async (jobID: string) => {
        const job = props.jobs.find(j => j.jobID === jobID && !j.replyTarget);
        if (!job || !props.onEditDashboard) return;

        try {
            // Get job parameters
            const parameters = await props.jobService.getJobParameters(jobID);

            // Extract SQL queries (SQL 1-24)
            const sqlQueries: string[] = [];
            for (let i = 1; i <= 24; i++) {
                sqlQueries.push(parameters[`SQL ${i}`] || "");
            }

            // Extract sizes (Size 1-24) and convert to number array
            const sizes: number[] = [];
            for (let i = 1; i <= 24; i++) {
                const sizeValue = parameters[`Size ${i}`];
                sizes.push(sizeValue ? parseInt(sizeValue) || 0 : 0);
            }

            // Extract dimensions
            const dimensions = parameters["Dimensions"] ? parseInt(parameters["Dimensions"]) || 0 : 0;

            // Use job alias directly as the dashboard title
            const title = job.alias;

            // Clear selected job to show SQLInputView
            job.page.selectedJob = undefined;

            // Call the callback with initial values
            props.onEditDashboard(jobID, { sqlQueries, sizes, dimensions, title });
        } catch (error) {
            console.error("Failed to load dashboard parameters:", error);
            notification.error({
                message: 'Failed to load dashboard',
                description: 'Could not load dashboard parameters. Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    }, [props.jobService, props.jobs, props.onEditDashboard]);

    const handleRegenerate = useCallback(async (jobID: string) => {
        const job = props.jobs.find(j => j.jobID === jobID && !j.replyTarget);
        if (!job) return;

        notification.info({
            message: 'Regenerating dashboard',
            description: 'Please wait while the dashboard is being regenerated...',
            placement: 'bottomLeft',
            duration: 0
        });

        try {
            const newJobInfo = await props.jobService.regenerateJob(jobID);
            // Reload jobs to get the new job
            const page = job.page;
            await page.reloadJobs();
            // Select the new job
            const newJob = page.jobs.find(j => j.jobID === newJobInfo.jobID);
            if (newJob) {
                page.selectedJob = newJob;
            }
            notification.destroy();
            notification.success({
                message: 'Dashboard regenerated',
                description: 'The dashboard is being regenerated.',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            console.error("Failed to regenerate dashboard:", error);
            notification.destroy();
            notification.error({
                message: 'Failed to regenerate dashboard',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    }, [props.jobService, props.jobs]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (streamContextMenuRef.current && !streamContextMenuRef.current.contains(event.target as Node)) {
                setStreamContextMenu({ visible: false, x: 0, y: 0, jobID: null });
                props.setAnyContextMenuOpen(false);
            }
            if (renamingStream && renameFormRef.current && !renameFormRef.current.contains(event.target as Node)) {
                handleRenameCancel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props, renamingStream, handleRenameCancel]);

    return (
        <div className="flex-1 flex-column" style={{ padding: "0.5em", paddingRight: "0.2em", position: "relative", background: themeColors.backgroundColorSecondary, height: "100%" }}>
            <style>{`
                .job-list-scroll::-webkit-scrollbar {
                    width: 10px;
                }
                .job-list-scroll::-webkit-scrollbar-track {
                    background: ${themeColors.backgroundColorSecondary};
                }
                .job-list-scroll::-webkit-scrollbar-thumb {
                    background: ${darkMode ? themeColors.borderColorSecondary : "#b0b0b0"};
                    border-radius: 5px;
                }
                .job-list-scroll::-webkit-scrollbar-thumb:hover {
                    background: ${themeColors.borderColor};
                }
                .job-list-select-dropdown .ant-select-item {
                    padding: 1px 6px !important;
                }
                ${darkMode ? `
                    .job-list-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                        border-color: ${themeColors.borderColor} !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                        outline: none !important;
                    }
                    .job-list-select-dropdown,
                    .job-list-select-dropdown > * {
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .job-list-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .job-list-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .job-list-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .job-list-select .ant-select-arrow {
                        display: none !important;
                    }
                    .job-list-select .ant-select-clear {
                        color: ${themeColors.textColor} !important;
                        background-color: transparent !important;
                    }
                    .job-list-select .ant-select-clear:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select .ant-select-selection-search-input {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-item-remove {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-item-remove:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select .ant-tag-close-icon {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-tag-close-icon:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-empty {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-empty {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-empty-description {
                        color: ${themeColors.textColorMuted} !important;
                    }
                ` : `
                    .job-list-select .ant-select-arrow {
                        display: none !important;
                    }
                `}
            `}</style>
            <div style={{ marginBottom: "0.5em", paddingRight: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5em" }}>
                    <ConditionalTooltip title={props.isCollapsed ? "Expand panel" : "Collapse panel"}>
                        <div onClick={props.onToggleCollapse} style={{ cursor: "pointer" }}>
                            <DashStreamLogo color={themeColors.primaryColor} />
                        </div>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="New Dashboard">
                        <button
                            onClick={props.onNewStream}
                            style={{
                                border: "none",
                                backgroundColor: "transparent",
                                color: themeColors.primaryColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: "34px",
                                padding: "0",
                                marginTop: "0.075em"
                            }}
                        >
                            <PlusCircleFilled />
                        </button>
                    </ConditionalTooltip>
                </div>
                <ConditionalTooltip title="Filter threads by free text or add tags to narrow results">
                    <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder="Search by text and/or tags..."
                        maxTagCount='responsive'
                        value={searchValues}
                        size="large"
                        onChange={setSearchValues}
                        allowClear={true}
                        disabled={props.isCollapsed}
                        tagRender={tagRender}
                        popupClassName="job-list-select-dropdown"
                        className="job-list-select"
                    >
                        {props.tagList.map(tag => (
                            <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                        ))}
                    </Select>
                </ConditionalTooltip>
            </div>
            <div ref={jobList} className="flex-1 scroll job-list-scroll" style={{ overflow: "auto", scrollbarGutter: "stable" }}>
                {
                    originalJobs
                        .filter(originalJob => {
                            // Check if dashboard name matches search criteria
                            if (searchValues.length === 0) return true;

                            const textSearchValues = searchValues.filter(value => !props.tagList.includes(value));
                            const tagSearchValues = searchValues.filter(value => props.tagList.includes(value));

                            const aliasMatches = textSearchValues.length === 0 ||
                                textSearchValues.every(value => originalJob.alias.toLowerCase().includes(value.toLowerCase()));

                            const tagMatches = tagSearchValues.length === 0 ||
                                (originalJob.tags && tagSearchValues.every(value => originalJob.tags!.includes(value)));

                            return aliasMatches && tagMatches;
                        })
                        .map(originalJob => {
                            const isSelected = originalJob.isSelected;

                            return (
                                <div
                                    key={originalJob.jobID}
                                    style={{
                                        backgroundColor: isSelected ? (darkMode ? themeColors.selectedBackground || themeColors.primaryColor + "33" : "white") : "transparent",
                                        border: isSelected ? `1px solid ${darkMode ? themeColors.primaryColor : themeColors.borderColor}` : "1px solid transparent",
                                        borderRadius: isSelected ? "5px" : "0",
                                        marginBottom: "2px"
                                    }}
                                >
                                    {/* Dashboard Row */}
                                    {renamingStream && renamingStream.jobID === originalJob.jobID ? (
                                        <div
                                            ref={renameFormRef}
                                            style={{
                                                padding: "0.3em 0.5em",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <input
                                                ref={renameInputRef}
                                                type="text"
                                                value={renamingStream.alias}
                                                onChange={(e) => setRenamingStream({ ...renamingStream, alias: e.target.value })}
                                                onKeyDown={handleRenameKeyDown}
                                                style={{
                                                    flex: 1,
                                                    fontSize: "0.95em",
                                                    padding: "0",
                                                    border: `1px solid ${themeColors.primaryColor}`,
                                                    backgroundColor: themeColors.backgroundColor,
                                                    color: themeColors.textColor,
                                                    outline: "none",
                                                    margin: 0,
                                                    lineHeight: "1.52",
                                                    height: "1.52em"
                                                }}
                                            />
                                            <ConditionalTooltip title="Submit">
                                                <button
                                                    onClick={handleRenameSubmit}
                                                    style={{
                                                        fontSize: "0.85em",
                                                        backgroundColor: themeColors.primaryColor,
                                                        color: "white",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        height: "1.7em",
                                                        width: "1.7em",
                                                        border: "none",
                                                        alignSelf: "flex-start",
                                                        flexShrink: 0,
                                                        marginLeft: "0.5em"
                                                    }}
                                                >
                                                    <CheckOutlined />
                                                </button>
                                            </ConditionalTooltip>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                padding: "0.3em 0.5em",
                                                display: "flex",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                userSelect: "none"
                                            }}
                                            onClick={() => originalJob.isSelected = true}
                                            onContextMenu={(e) => handleStreamRightClick(e, originalJob.jobID)}
                                            onMouseEnter={(e) => {
                                                const target = e.currentTarget;
                                                const tagButton = target.querySelector('.dashboard-tag-button') as HTMLElement;
                                                const scheduleButton = target.querySelector('.dashboard-schedule-button') as HTMLElement;
                                                if (tagButton) {
                                                    tagButton.style.visibility = 'visible';
                                                }
                                                if (scheduleButton) {
                                                    scheduleButton.style.visibility = 'visible';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                const target = e.currentTarget;
                                                const tagButton = target.querySelector('.dashboard-tag-button') as HTMLElement;
                                                const scheduleButton = target.querySelector('.dashboard-schedule-button') as HTMLElement;
                                                const hasTags = originalJob.tags && originalJob.tags.length > 0;
                                                const hasSchedule = originalJob.scheduleTime && originalJob.scheduleType;
                                                const tagPopoverOpen = openTagPopovers.has(originalJob.jobID);
                                                const scheduleModalOpen = scheduleModal.isOpen && scheduleModal.jobID === originalJob.jobID;

                                                if (tagButton && !hasTags && !tagPopoverOpen) {
                                                    tagButton.style.visibility = 'hidden';
                                                }
                                                if (scheduleButton && !hasSchedule && !scheduleModalOpen) {
                                                    scheduleButton.style.visibility = 'hidden';
                                                }
                                            }}
                                        >
                                            <ConditionalTooltip title={originalJob.alias?.trim() || ''}>
                                                <span
                                                    style={{ fontSize: "0.95em", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: themeColors.textColor }}
                                                >
                                                    {originalJob.alias}
                                                </span>
                                            </ConditionalTooltip>
                                            <span
                                                className="dashboard-tag-button"
                                                style={{
                                                    visibility: ((originalJob.tags && originalJob.tags.length > 0) || openTagPopovers.has(originalJob.jobID)) ? 'visible' : 'hidden',
                                                    marginLeft: "0.5em",
                                                    display: "flex",
                                                    alignItems: "center"
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <TagView
                                                    model={originalJob.tagViewModel}
                                                    tagList={props.tagList}
                                                    onOpenChange={(isOpen) => {
                                                        setOpenTagPopovers(prev => {
                                                            const newSet = new Set(prev);
                                                            if (isOpen) {
                                                                newSet.add(originalJob.jobID);
                                                            } else {
                                                                newSet.delete(originalJob.jobID);
                                                            }
                                                            return newSet;
                                                        });
                                                    }}
                                                />
                                            </span>
                                            <span
                                                className="dashboard-schedule-button"
                                                style={{
                                                    visibility: ((originalJob.scheduleTime && originalJob.scheduleType) || (scheduleModal.isOpen && scheduleModal.jobID === originalJob.jobID)) ? 'visible' : 'hidden',
                                                    marginLeft: "0.5em",
                                                    display: "flex",
                                                    alignItems: "center"
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setScheduleModal({ isOpen: true, jobID: originalJob.jobID });
                                                }}
                                            >
                                                <ConditionalTooltip title="Schedule dashboard regeneration">
                                                    <button
                                                        style={{
                                                            background: "transparent",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            fontSize: "20px",
                                                            color: darkMode ? "#888888" : "#AFAFAF",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            padding: "0",
                                                            transition: "color 0.2s"
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                                                        }}
                                                    >
                                                        <CalendarOutlined />
                                                    </button>
                                                </ConditionalTooltip>
                                            </span>
                                            </div>
                                    )}
                                </div>
                            );
                        })
                }
            </div>
            {streamContextMenu.visible && streamContextMenu.jobID && (() => {
                const streamJob = originalJobs.find(job => job.jobID === streamContextMenu.jobID);
                return streamJob ? (
                    <StreamContextMenuView
                        model={streamJob}
                        contextMenu={{ visible: streamContextMenu.visible, x: streamContextMenu.x, y: streamContextMenu.y }}
                        setContextMenu={(menu) => setStreamContextMenu(prev => ({ ...prev, ...menu }))}
                        contextMenuRef={streamContextMenuRef}
                        onRename={() => handleRenameStream(streamContextMenu.jobID!)}
                        onEdit={() => handleEditDashboard(streamContextMenu.jobID!)}
                        onRegenerate={() => handleRegenerate(streamContextMenu.jobID!)}
                    />
                ) : null;
            })()}
            {scheduleModal.isOpen && scheduleModal.jobID && (() => {
                const scheduledJob = originalJobs.find(job => job.jobID === scheduleModal.jobID);
                return scheduledJob ? (
                    <ScheduleRegenModalView
                        isOpen={scheduleModal.isOpen}
                        onClose={() => setScheduleModal({ isOpen: false, jobID: null })}
                        initialScheduleTime={scheduledJob.scheduleTime}
                        initialScheduleType={scheduledJob.scheduleType}
                        onSchedule={async (schedule: ScheduleData) => {
                            try {
                                if (schedule.clearSchedule) {
                                    await props.jobService.scheduleJob(scheduleModal.jobID!, null, null);
                                    notification.success({
                                        message: 'Schedule stopped',
                                        description: 'The schedule has been cleared.',
                                        placement: 'bottomLeft',
                                        duration: 3
                                    });
                                } else {
                                    const scheduleTime = schedule.date.toISOString();
                                    const scheduleType = schedule.frequency;
                                    await props.jobService.scheduleJob(scheduleModal.jobID!, scheduleTime, scheduleType);
                                    notification.success({
                                        message: scheduledJob.scheduleTime && scheduledJob.scheduleType ? 'Schedule updated' : 'Schedule created',
                                        description: `Regeneration scheduled for ${schedule.date.format('YYYY-MM-DD h:mm A')} (${schedule.frequency}).`,
                                        placement: 'bottomLeft',
                                        duration: 3
                                    });
                                }
                                // Reload jobs to update schedule status
                                const pageViewModel = scheduledJob.page;
                                await pageViewModel.reloadJobs();
                            } catch (error) {
                                notification.error({
                                    message: 'Failed to update schedule',
                                    description: error instanceof Error ? error.message : 'Please try again.',
                                    placement: 'bottomLeft',
                                    duration: 3
                                });
                                throw error;
                            }
                        }}
                        darkMode={darkMode}
                    />
                ) : null;
            })()}
        </div>
    );
}
